<script setup lang="ts">
import { debounce, Selector } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useGetAdvertiserListRequest } from '@/api'
import type { FilterType } from '@/lib/types/common.types'

const props = withDefaults(
  defineProps<{
    advertiserGroupId?: number
    shouldTruncateText?: boolean
    label?: string
    size?: 'default' | 'large'
    isDisabled?: boolean
  }>(),
  { label: 'Advertiser', size: 'default', isDisabled: false },
)
const emits = defineEmits(['update:advertiser'])
const advertiser = defineModel<FilterType['advertiser']>('advertiser')
const query = ref('')
const advertiserGroupIdRef = computed(() => props.advertiserGroupId)
const { data: advertiserOptions, isLoading: isAdvertisersLoading } = useGetAdvertiserListRequest(advertiserGroupIdRef, query)

const advertiserLabel = computed(
  () => advertiser.value?.label || advertiserOptions.value.find((item) => item.id === advertiser.value?.id)?.label,
)

const handleSearchQuery = debounce((value: string) => (query.value = value), 300)
const handleSelection = (item: { id: number; label?: string } | null) => {
  emits('update:advertiser', item ? { id: item.id, label: item.label ?? '' } : undefined)
}
const handleVisible = () => (query.value = '')
</script>

<template>
  <Selector
    :options="advertiserOptions"
    :selected-option="advertiser ?? null"
    :selected-label="advertiserLabel"
    :label="label"
    :size="size"
    :is-disabled="isDisabled"
    placeholder="Search advertisers"
    :loading="isAdvertisersLoading"
    :should-truncate-text="shouldTruncateText"
    :query="query"
    show-clear-button
    @search="handleSearchQuery"
    @update:selected-option="handleSelection"
    @visible-change="handleVisible"
  />
</template>
